import { Card } from '@mui/material';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { usePolicy } from '../../api/PolicyApi';
import RenewalsApi from '../../api/RenewalsApi';
import { AuthData } from '../../context/auth';
import { PolicyData, withPolicyContext } from '../../context/policy';
import {
	formatName,
	getOverrideResourcesAsOfTimestampAtTime,
	getPolicyStatus,
	getResourcesAsOfTimestampAtTime,
} from '../../util/util';
import CashBalanceDisplayPill from '../cashBalance/components/CashBalanceDisplayPill';
import { StyledSectionContainer } from '../common';
import DisplayCard from '../DisplayCard';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
} from '../fastForm/data/constants';
import { getCurrentGraceLapse } from '../search/util';

export const POLICY_OVERVIEW_HEIGHT = 244;

interface Props {
	policyData: PolicyData;
	authData: AuthData;
}

const PolicyOverview = (props: Props) => {
	const { policyData, authData } = props;
	const { hasPermission } = authData;

	const {
		quoteUpdateObj,
		setQuoteUpdateObj,
		selectedQuote,
		policy,
		graceLapse,
		policyViewTimestamp,
	} = policyData;

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { data: renewalsResponse } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		enabled: hasPermission('renewal.fetch'),
		staleTime: 1000 * 60 * 10, // 10 minutes
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	// TODO: cleanup (e.g: rename) once moving away from policy context
	const { data: policyResponse } = usePolicy(
		policy.locator,
		authData?.auth?.authorizationToken,
	);

	const initialQuoteName = !selectedQuote
		? quoteUpdateObj.name
		: selectedQuote.name;
	const [quoteName, setQuoteName] = useState(initialQuoteName);

	useEffect(() => {
		if (selectedQuote) {
			const updatedQuoteName = selectedQuote.name;
			setQuoteName(updatedQuoteName);
		}
	}, [selectedQuote]);

	const debouncedUpdateQuoteName = useCallback(
		_.debounce((name: string) => {
			quoteUpdateObj.name = name;
			setQuoteUpdateObj(quoteUpdateObj);
		}, 200),
		[quoteUpdateObj],
	);

	const updateQuoteName = (name: string) => {
		setQuoteName(name);
		debouncedUpdateQuoteName(name);
	};

	const state =
		policy.issuedTimestamp || !selectedQuote
			? getPolicyStatus(policy, getCurrentGraceLapse(graceLapse))
			: selectedQuote.state
					.split(' ')
					.map((s) => s.charAt(0).toUpperCase() + s.substring(1))
					.join(' ');

	const policyViewTimestampInt = parseInt(policyViewTimestamp, 10);

	const resourcesAsOfTimestamp = policy.issuedTimestamp
		? getResourcesAsOfTimestampAtTime(
				policy.modifications,
				policyViewTimestampInt,
		  )
		: policy.modifications[0]?.resourcesAsOfTimestamp;

	let overrideResourcesAsOfTimestampAtTime =
		getOverrideResourcesAsOfTimestampAtTime(
			policy,
			renewalsResponse?.data,
			policyViewTimestampInt,
		);

	const renderTitleCard = () => {
		if (selectedQuote && !policy.issuedTimestamp) {
			const disabled =
				!authData.hasPermission('quote.update') ||
				selectedQuote.state !== 'draft';
			const isEmpty = !quoteName;
			const helperText = isEmpty ? 'Quote name cannot be empty' : null;
			return (
				<>
					{disabled ? (
						renderDetailCard('Quote Name', quoteName)
					) : (
						<Card>
							<TextField
								className={'quote-name'}
								label='Quote Name'
								error={isEmpty}
								helperText={helperText}
								value={quoteName}
								disabled={disabled}
								onChange={(e) => updateQuoteName(e.target.value)}
							/>
						</Card>
					)}
					{renderDetailCard('Quote Id', selectedQuote.locator)}
				</>
			);
		}
		return null;
	};

	const renderDetailCard = (title: string, data: string | JSX.Element) => {
		return <DisplayCard title={title} value={data} />;
	};

	return (
		<StyledSectionContainer>
			<Stack spacing={2}>
				<Typography id='policy_id' variant='h3' gutterBottom>
					Policy {policy.displayId}
				</Typography>
				<Stack spacing={2} direction='row'>
					{policyResponse?.cashBalance &&
						hasPermission('cashBalance.fetch') &&
						renderDetailCard(
							'Cash Balance',
							<CashBalanceDisplayPill
								amount={policyResponse.cashBalance.amount}
								currencyCode={policyResponse.cashBalance.currency}
							/>,
						)}
					{renderTitleCard()}
					{renderDetailCard('Product', formatName(policy.productName))}
					{renderDetailCard(
						'Date Created',
						authData.getZonedFormattedDateString(policy.createdTimestamp),
					)}
					{renderDetailCard(
						'Last Modified',
						authData.getZonedFormattedDateString(policy.updatedTimestamp),
					)}
					{renderDetailCard('Status', state)}
					{resourcesAsOfTimestamp &&
						renderDetailCard(
							'Resource Group',
							`${authData.getZonedFormattedDateString(
								resourcesAsOfTimestamp,
								DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.MINUTE],
							)} ${
								overrideResourcesAsOfTimestampAtTime === resourcesAsOfTimestamp
									? ' (Override)'
									: ''
							}`,
						)}
				</Stack>
			</Stack>
		</StyledSectionContainer>
	);
};

export default withPolicyContext<Props>(PolicyOverview);
